<template>
  <a-card
      :bordered="true"
      class="ei-scale-rows-card"
  >
    <div class="ei-scale-rows-card__header">
      <h3 class="ei-scale-rows-card__title">
        {{ title }}
      </h3>

      <QuestionCircleOutlined class="ei-scale-rows-card__help-icon" />
    </div>

    <EiScaleRowsTable
        :sections="normalizedSections"
        :min="min"
        :max="max"
        :mode="mode"
        :scale-height="scaleHeight"
        :compare-scale-height="compareScaleHeight"
        :row-gap="rowGap"
        :show-difference="showDifference"
        :show-track-values="showTrackValues"
        :show-scale-after-each-section="showScaleAfterEachSection"
        :show-first-section-title="showFirstSectionTitle"
    />
  </a-card>
</template>

<script>
import { computed } from 'vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'

import EiScaleRowsTable from "@/components/Analytics/EiScaleRowsTable.vue";

const RED_COLOR = '#F55565'
const GREEN_COLOR = '#8BD05B'

export default {
  name: 'EiScaleRowsCard',

  components: {
    QuestionCircleOutlined,
    EiScaleRowsTable,
  },

  props: {
    title: {
      type: String,
      default: '',
    },

    sections: {
      type: Array,
      default: () => [],
    },

    min: {
      type: Number,
      default: 50,
    },

    max: {
      type: Number,
      default: 150,
    },

    mode: {
      type: String,
      default: 'single',
      validator: (value) => ['single', 'compare'].includes(value),
    },

    scaleHeight: {
      type: Number,
      default: 8,
    },

    compareScaleHeight: {
      type: Number,
      default: 8,
    },

    rowGap: {
      type: Number,
      default: 12,
    },

    showDifference: {
      type: Boolean,
      default: false,
    },

    showTrackValues: {
      type: Boolean,
      default: false,
    },

    showScaleAfterEachSection: {
      type: Boolean,
      default: false,
    },

    showFirstSectionTitle: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const normalizedSections = computed(() => {
      return (Array.isArray(props.sections) ? props.sections : []).map((section, sectionIndex) => ({
        ...section,
        key: section.key || `section-${sectionIndex}`,
        rows: Array.isArray(section.rows)
            ? section.rows.map((row, rowIndex) => {
              const value = toNumber(row.value)

              return {
                ...row,
                key: row.key || `row-${rowIndex}`,
                label: row.label || '',
                value,
                color: row.color || getColorByValue(value),
              }
            })
            : [],
      }))
    })

    return {
      normalizedSections,
    }
  },
}

function toNumber(value) {
  const number = Number(value)

  return Number.isFinite(number) ? number : 0
}

function getColorByValue(value) {
  return Number(value) >= 100 ? GREEN_COLOR : RED_COLOR
}
</script>

<style scoped lang="scss">
.ei-scale-rows-card {
  min-width: 0;
  height: 100%;
  border-radius: 8px;
}

.ei-scale-rows-card :deep(.ant-card-body) {
  height: 100%;
  padding: 18px 16px 16px;
}

.ei-scale-rows-card__header {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.ei-scale-rows-card__title {
  margin: 0;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-rows-card__help-icon {
  flex: 0 0 auto;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.25);
  font-size: 14px;
}

@media (max-width: $mobile-breakpoint) {
  .ei-scale-rows-card :deep(.ant-card-body) {
    padding: 14px 12px;
  }
}
</style>